import React, { useEffect, useRef, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { toast } from "sonner@2.0.3";
import { Save, Upload, Trash, ArrowUp, ArrowDown, Rocket } from "lucide-react";
import { mediaService, getPublicProjects } from "../../services/Project";
import {
  SiteSettings,
  SiteSettingKey,
  DEFAULT_SITE_SETTINGS,
  getAdminSettings,
  updateSetting,
  getPublishStatus,
  publishSite,
  safeUrl,
  PublishStatus,
} from "../../services/SiteSettings";

const toLines = (text: string) => text.split("\n").map((l) => l.trim()).filter(Boolean);

const HOME_SLOTS = 3;
const AUTO = "auto";

type ProjectOption = { id: number; title: string; year?: string };

export function ManageSiteSettings() {
  const [settings, setSettings] = useState<SiteSettings>(DEFAULT_SITE_SETTINGS);
  const [phonesText, setPhonesText] = useState("");
  const [hoursText, setHoursText] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<SiteSettingKey | null>(null);
  const [uploading, setUploading] = useState(false);
  const [publish, setPublish] = useState<PublishStatus | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [accUploading, setAccUploading] = useState(false);
  const accInputRef = useRef<HTMLInputElement>(null);
  // null = add a new image; a number = replace the image at that index
  const accReplaceIndex = useRef<number | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const s = await getAdminSettings();
        setSettings(s);
        setPhonesText(s.contact.phones.join("\n"));
        setHoursText(s.contact.officeHours.join("\n"));
      } catch (err: any) {
        toast.error(err?.message || "Failed to load settings");
      } finally {
        setLoading(false);
      }
      getPublishStatus().then(setPublish).catch(() => setPublish(null));
      getPublicProjects()
        .then((list: any[]) =>
          setProjects(
            (list || [])
              .filter((p) => p?.id != null)
              .map((p) => ({ id: Number(p.id), title: p.title, year: p.year }))
          )
        )
        .catch(() => toast.error("Failed to load projects"));
    })();
  }, []);

  const setField = <K extends SiteSettingKey>(key: K, field: keyof SiteSettings[K], value: any) =>
    setSettings((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));

  const save = async (key: SiteSettingKey) => {
    let value: any = settings[key];
    if (key === "contact") {
      value = { ...settings.contact, phones: toLines(phonesText), officeHours: toLines(hoursText) };
      if (!value.email || value.phones.length === 0) {
        toast.error("Email and at least one phone are required");
        return;
      }
    }
    if (key === "social") {
      const invalid = Object.values(settings.social).some((u) => u && !/^https:\/\//i.test(u.trim()));
      if (invalid) {
        toast.error("Social links must start with https://");
        return;
      }
    }
    if (key === "hero" && settings.hero.images.length === 0) {
      toast.error("Add at least one hero image");
      return;
    }
    if (key === "home") {
      const ids = settings.home.featuredProjectIds || [];
      if (new Set(ids).size !== ids.length) {
        toast.error("Each featured project must be different");
        return;
      }
      if (settings.home.accomplishments.length === 0) {
        toast.error("Add at least one accomplishment image");
        return;
      }
      value = {
        ...settings.home,
        featuredProjectIds: ids.slice(0, HOME_SLOTS),
        accomplishments: settings.home.accomplishments.slice(0, HOME_SLOTS),
      };
    }
    setSaving(key);
    try {
      await updateSetting(key, value);
      toast.success("Saved. Click \"Publish website\" to update search engines.");
    } catch (err: any) {
      toast.error(err?.message || "Failed to save");
    } finally {
      setSaving(null);
    }
  };

  const handleHeroUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be smaller than 5 MB");
      return;
    }
    setUploading(true);
    try {
      const res = await mediaService.uploadMedia([{ file }], { name: "hero" });
      const asset = res?.uploadedAssets?.[0];
      if (!asset?.success || !asset.src) throw new Error(asset?.error || "Upload failed");
      setField("hero", "images", [...settings.hero.images, asset.src]);
    } catch (err: any) {
      toast.error(err?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const moveImage = (index: number, delta: number) => {
    const images = [...settings.hero.images];
    const target = index + delta;
    if (target < 0 || target >= images.length) return;
    [images[index], images[target]] = [images[target], images[index]];
    setField("hero", "images", images);
  };

  const removeImage = (index: number) =>
    setField("hero", "images", settings.hero.images.filter((_, i) => i !== index));

  const setFeatured = (slot: number, value: string) => {
    const ids = [...(settings.home.featuredProjectIds || [])];
    if (value === AUTO) ids.splice(slot, 1);
    else ids[slot] = Number(value);
    setField("home", "featuredProjectIds", ids.filter((id) => Number.isFinite(id)));
  };

  const handleAccomplishmentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const replaceIndex = accReplaceIndex.current;
    accReplaceIndex.current = null;
    if (!file) return;
    if (replaceIndex == null && settings.home.accomplishments.length >= HOME_SLOTS) {
      toast.error(`Only ${HOME_SLOTS} images are shown. Remove one first.`);
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be smaller than 5 MB");
      return;
    }
    setAccUploading(true);
    try {
      const res = await mediaService.uploadMedia([{ file }], { name: "accomplishment" });
      const asset = res?.uploadedAssets?.[0];
      if (!asset?.success || !asset.src) throw new Error(asset?.error || "Upload failed");
      const items = [...settings.home.accomplishments];
      if (replaceIndex == null) items.push({ src: asset.src, alt: "" });
      else items[replaceIndex] = { ...items[replaceIndex], src: asset.src };
      setField("home", "accomplishments", items);
      toast.success("Image uploaded. Click \"Save home highlights\" to apply.");
    } catch (err: any) {
      toast.error(err?.message || "Upload failed");
    } finally {
      setAccUploading(false);
    }
  };

  const updateAccomplishment = (index: number, alt: string) =>
    setField(
      "home",
      "accomplishments",
      settings.home.accomplishments.map((img, i) => (i === index ? { ...img, alt } : img))
    );

  const moveAccomplishment = (index: number, delta: number) => {
    const items = [...settings.home.accomplishments];
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    [items[index], items[target]] = [items[target], items[index]];
    setField("home", "accomplishments", items);
  };

  const pickAccomplishmentFile = (replaceIndex: number | null) => {
    accReplaceIndex.current = replaceIndex;
    accInputRef.current?.click();
  };

  const removeAccomplishment = (index: number) => {
    if (!window.confirm("Delete this photo from the home page?")) return;
    setField("home", "accomplishments", settings.home.accomplishments.filter((_, i) => i !== index));
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const res = await publishSite();
      setPublish((prev) => ({ configured: true, ...prev, ...res }));
      toast.success("Website rebuild started. Changes go live in a few minutes.");
    } catch (err: any) {
      toast.error(err?.message || "Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  if (loading) return <p className="text-sm text-gray-500">Loading...</p>;

  const { contact, social, hero, home } = settings;
  const featuredIds = home.featuredProjectIds || [];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Publish website</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p className="text-gray-600">
            Saved changes appear for visitors immediately. Publishing rebuilds the static pages so Google and AI
            search engines see the latest content.
          </p>
          {publish && !publish.configured && (
            <p className="text-amber-600">Publishing is not configured on the server.</p>
          )}
          {publish?.lastPublishedAt && (
            <p className="text-gray-500">Last published: {new Date(publish.lastPublishedAt).toLocaleString()}</p>
          )}
          <Button onClick={handlePublish} disabled={publishing} className="cursor-pointer">
            <Rocket className="w-4 h-4 mr-2" />
            {publishing ? "Publishing..." : "Publish website"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Contact information</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label htmlFor="c-name">Organization name</Label>
            <Input id="c-name" value={contact.name} onChange={(e) => setField("contact", "name", e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="c-display">Address (as displayed)</Label>
            <Input id="c-display" value={contact.addressDisplay} onChange={(e) => setField("contact", "addressDisplay", e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="c-street">Street address (for search engines)</Label>
            <Input id="c-street" value={contact.streetAddress} onChange={(e) => setField("contact", "streetAddress", e.target.value)} />
          </div>
          <div>
            <Label htmlFor="c-city">City</Label>
            <Input id="c-city" value={contact.locality} onChange={(e) => setField("contact", "locality", e.target.value)} />
          </div>
          <div>
            <Label htmlFor="c-postal">Postal code</Label>
            <Input id="c-postal" value={contact.postalCode} onChange={(e) => setField("contact", "postalCode", e.target.value)} />
          </div>
          <div>
            <Label htmlFor="c-country">Country</Label>
            <Input id="c-country" value={contact.country} onChange={(e) => setField("contact", "country", e.target.value)} />
          </div>
          <div>
            <Label htmlFor="c-email">Email</Label>
            <Input id="c-email" type="email" value={contact.email} onChange={(e) => setField("contact", "email", e.target.value)} />
          </div>
          <div>
            <Label htmlFor="c-lat">Latitude</Label>
            <Input id="c-lat" value={contact.latitude} onChange={(e) => setField("contact", "latitude", e.target.value)} />
          </div>
          <div>
            <Label htmlFor="c-lng">Longitude</Label>
            <Input id="c-lng" value={contact.longitude} onChange={(e) => setField("contact", "longitude", e.target.value)} />
          </div>
          <div>
            <Label htmlFor="c-phones">Phone numbers (one per line)</Label>
            <Textarea id="c-phones" rows={3} value={phonesText} onChange={(e) => setPhonesText(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="c-hours">Office hours (one per line)</Label>
            <Textarea id="c-hours" rows={3} value={hoursText} onChange={(e) => setHoursText(e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <Button onClick={() => save("contact")} disabled={saving === "contact"} className="cursor-pointer">
              <Save className="w-4 h-4 mr-2" />
              {saving === "contact" ? "Saving..." : "Save contact"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Social links</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {(["facebook", "linkedin", "twitter"] as const).map((k) => (
            <div key={k}>
              <Label htmlFor={`s-${k}`} className="capitalize">{k}</Label>
              <Input id={`s-${k}`} placeholder="https://..." value={social[k]} onChange={(e) => setField("social", k, e.target.value)} />
            </div>
          ))}
          <Button onClick={() => save("social")} disabled={saving === "social"} className="cursor-pointer">
            <Save className="w-4 h-4 mr-2" />
            {saving === "social" ? "Saving..." : "Save social links"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Home page hero</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="h-title">Headline</Label>
            <Input id="h-title" value={hero.title} onChange={(e) => setField("hero", "title", e.target.value)} />
          </div>
          <div>
            <Label htmlFor="h-tagline">Tagline</Label>
            <Input id="h-tagline" value={hero.tagline} onChange={(e) => setField("hero", "tagline", e.target.value)} />
          </div>
          <div>
            <Label>Slideshow images (first image loads first)</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
              {hero.images.map((src, i) => (
                <div key={`${src}-${i}`} className="border rounded-md p-2 space-y-2">
                  <img src={safeUrl(src)} alt={`Hero slide ${i + 1}`} className="w-full h-24 object-cover rounded" />
                  <div className="flex justify-between">
                    <Button type="button" size="sm" variant="outline" onClick={() => moveImage(i, -1)} disabled={i === 0} aria-label="Move up">
                      <ArrowUp className="w-3 h-3" />
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => moveImage(i, 1)} disabled={i === hero.images.length - 1} aria-label="Move down">
                      <ArrowDown className="w-3 h-3" />
                    </Button>
                    <Button type="button" size="sm" variant="destructive" onClick={() => removeImage(i)} aria-label="Remove">
                      <Trash className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3">
              <Label htmlFor="h-upload" className="inline-flex items-center cursor-pointer text-sm text-blue-600">
                <Upload className="w-4 h-4 mr-1" />
                {uploading ? "Uploading..." : "Add image"}
              </Label>
              <input id="h-upload" type="file" accept="image/*" className="hidden" onChange={handleHeroUpload} disabled={uploading} />
            </div>
          </div>
          <Button onClick={() => save("hero")} disabled={saving === "hero" || uploading} className="cursor-pointer">
            <Save className="w-4 h-4 mr-2" />
            {saving === "hero" ? "Saving..." : "Save hero"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Home page highlights</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label>Recently Completed Projects (up to {HOME_SLOTS})</Label>
            <p className="text-xs text-gray-500 mb-2">
              Leave all slots on "Automatic" to show the {HOME_SLOTS} most recent projects.
            </p>
            <div className="space-y-2">
              {Array.from({ length: HOME_SLOTS }, (_, slot) => {
                const current = featuredIds[slot];
                const missing = current != null && !projects.some((p) => p.id === current);
                return (
                  <div key={slot} className="flex items-center gap-2">
                    <span className="text-sm text-gray-500 w-6">{slot + 1}.</span>
                    <select
                      aria-label={`Featured project ${slot + 1}`}
                      className="flex-1 border rounded-md px-3 py-2 text-sm bg-white"
                      value={current != null ? String(current) : AUTO}
                      disabled={slot > featuredIds.length}
                      onChange={(e) => setFeatured(slot, e.target.value)}
                    >
                      <option value={AUTO}>Automatic</option>
                      {missing && <option value={String(current)}>Project #{current} (not found)</option>}
                      {projects.map((p) => (
                        <option
                          key={p.id}
                          value={String(p.id)}
                          disabled={p.id !== current && featuredIds.includes(p.id)}
                        >
                          {p.title}{p.year ? ` (${p.year})` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <Label>Our Accomplishments images (up to {HOME_SLOTS}, shown in this order)</Label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
              {home.accomplishments.map((img, i) => (
                <div key={`${img.src}-${i}`} className="border rounded-md p-2 space-y-2">
                  <img src={safeUrl(img.src)} alt={img.alt || `Accomplishment ${i + 1}`} className="w-full h-32 object-contain rounded bg-gray-50" />
                  <Input
                    aria-label={`Description for image ${i + 1}`}
                    placeholder="Short description (for accessibility and SEO)"
                    value={img.alt || ""}
                    onChange={(e) => updateAccomplishment(i, e.target.value)}
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={() => moveAccomplishment(i, -1)} disabled={i === 0} aria-label="Move left">
                      <ArrowUp className="w-3 h-3" />
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => moveAccomplishment(i, 1)} disabled={i === home.accomplishments.length - 1} aria-label="Move right">
                      <ArrowDown className="w-3 h-3" />
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => pickAccomplishmentFile(i)} disabled={accUploading}>
                      <Upload className="w-3 h-3 mr-1" />
                      Replace
                    </Button>
                    <Button type="button" size="sm" variant="destructive" onClick={() => removeAccomplishment(i)}>
                      <Trash className="w-3 h-3 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => pickAccomplishmentFile(null)}
                disabled={accUploading || home.accomplishments.length >= HOME_SLOTS}
                className="cursor-pointer"
              >
                <Upload className="w-4 h-4 mr-2" />
                {accUploading ? "Uploading..." : "Upload image"}
              </Button>
              {home.accomplishments.length >= HOME_SLOTS && (
                <span className="text-xs text-gray-500">
                  Maximum {HOME_SLOTS} images. Delete or replace one to upload another.
                </span>
              )}
              <input
                ref={accInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleAccomplishmentUpload}
              />
            </div>
          </div>

          <Button onClick={() => save("home")} disabled={saving === "home" || accUploading} className="cursor-pointer">
            <Save className="w-4 h-4 mr-2" />
            {saving === "home" ? "Saving..." : "Save home highlights"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
